import { SvgIcons } from '../../../../voyage-svg-icons/src/voyage-svg-icons'
import {
    NotificationTypesExtended,
    UnifiedNotification,
    activityLogNotification,
} from '../../../../../src/models/types/vessel-notifications.types'
import { GetFormattedDayMonth, GetUTCTimestamp } from '../../../../../src/utils/helpers'

export type NoticiationIcon = {
    name?: string // maersk icons
    customName?: keyof typeof SvgIcons // our custom icons
    bgColor: string
    color?: string
    size?: string
}
type RiskNotificationType =
    | NotificationTypesExtended.ParametricRoll
    | NotificationTypesExtended.HeavyWeather
    | NotificationTypesExtended.HeavyRolling
    | NotificationTypesExtended.HighAccelerationForce

// Notification to icon mapping
const IconType = {
    // Risk
    risk_heavyWeather: { customName: 'customHeavyweather', bgColor: '#ff5f00', color: '#ffffff' },
    risk_parametricRoll: {
        customName: 'customParametricroll',
        bgColor: '#f258d4',
        color: '#ffffff',
    },
    risk_heavyRolling: {
        customName: 'customParametricroll',
        bgColor: '#a880f5',
        color: '#ffffff',
    },
    risk_highAccelerationForce: {
        customName: 'customParametricroll',
        bgColor: '#c58f60',
        color: '#ffffff',
    },

    // Polar
    polar_risk: { customName: 'polarChart', bgColor: '#8b5cf6' },

    // Offline
    offline_passagePlanSubmission: {
        name: 'file-eye',
        bgColor: '#767676',
        color: '#ffffff',
    },

    // Other
    integratedGPS: { name: 'radar', bgColor: '#20abd1' },
    wniReport: { name: 'browser', bgColor: '#006bac' },
    basic_plan: { name: 'exclamation-circle', bgColor: 'rgb(211, 236, 249)' },
} as const

export interface ActionButton {
    label: string
    action: string // event name that will be dispatched
    data?: Record<string, any> // optional data to be passed with the event - check if this is needed
}

export const createOfflinePassagePlanNotification = (
    props: activityLogNotification,
): activityLogNotification => {
    return {
        title: 'Offline passage plan submission status',
        icon: IconType['offline_passagePlanSubmission'],
        ...props,
    }
}


const calculateDuration = (startTime: string, endTime: string): number => {
    const start = new Date(startTime).getTime()
    const end = new Date(endTime).getTime()
    const durationMs = end - start
    return Math.round(durationMs / (1000 * 60 * 60)) // Convert to hours
}

const getUtcDayMonth = (date: string): string => {
    const dateVal = new Date(date)
    const month = dateVal.toLocaleString('en-us', { month: 'short', timeZone: 'UTC' })
    return `${dateVal.getUTCDate()} ${month}`
}

export const createRiskNotification = (
    props: activityLogNotification,
    type: RiskNotificationType,
): activityLogNotification => {
    const data = props.details

    if (!data || !data.start_time) {
        console.error(
            '[createRiskNotification] Missing data or start_time:',
            data,
        )
        return props
    }

    const date = GetFormattedDayMonth(data.start_time)
    const time = GetUTCTimestamp(data.start_time)

    let description = ''
    let title = ''
    let icon: {}

    switch (type) {
        case NotificationTypesExtended.HeavyWeather:
            description = `Risk of Heavy weather foreseen on ${date} at ${time}.`
            icon = IconType['risk_heavyWeather']
            break
        case NotificationTypesExtended.ParametricRoll:
            const duration = data.end_time
                ? calculateDuration(data.start_time, data.end_time)
                : 0
            description = `Risk of Parametric roll foreseen on ${date} at ${time}${duration > 0 ? ` for ${duration} hours` : ''}.`
            icon = IconType['risk_parametricRoll']
            break
        case NotificationTypesExtended.HeavyRolling:
            description = `Risk of Heavy weather foreseen on ${date} at ${time}.`
            icon = IconType['risk_heavyRolling']
            break
        case NotificationTypesExtended.HighAccelerationForce:
            description = `Risk of high Acceleration Force foreseen on ${getUtcDayMonth(data.start_time)} at ${time} UTC.`
            icon = IconType['risk_highAccelerationForce']
            break
    }

    return {
        ...props,
        title,
        description,
        icon,
    }
}

export const createActivityLogItems = (
    notifications: UnifiedNotification[],
) => {
    const activityLog: activityLogNotification[] = []

    // TO DO: Add rest of notifications types here
    notifications.forEach((notification) => {
        switch (notification.type) {
            case NotificationTypesExtended.Sync:
                const syncNotification =
                    createOfflinePassagePlanNotification(notification)
                activityLog.push(syncNotification)
                break
            case NotificationTypesExtended.BasicPlan:
                activityLog.push({
                    ...notification,
                    icon: IconType['basic_plan'],
                })
                break
            case NotificationTypesExtended.HeavyWeather:
                const heavyWeatherNotification = createRiskNotification(
                    notification,
                    NotificationTypesExtended.HeavyWeather,
                )
                activityLog.push(heavyWeatherNotification)
                break
            case NotificationTypesExtended.ParametricRoll:
                const parametricRollNotification = createRiskNotification(
                    notification,
                    NotificationTypesExtended.ParametricRoll,
                )
                activityLog.push(parametricRollNotification)
                break
            case NotificationTypesExtended.HeavyRolling:
                const heavyRollingNotification = createRiskNotification(
                    notification,
                    NotificationTypesExtended.HeavyRolling,
                )
                activityLog.push(heavyRollingNotification)
                break
            case NotificationTypesExtended.HighAccelerationForce:
                const highAccelerationForceNotification = createRiskNotification(
                    notification,
                    NotificationTypesExtended.HighAccelerationForce,
                )
                activityLog.push(highAccelerationForceNotification)
                break
            default:
                return
        }
    })

    return activityLog
}
export type ActivityLogItem = activityLogNotification
